// src/pages/ProfileSettings.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { auth, db, functions } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
} from "firebase/firestore";
import { sendPasswordResetEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import TopNav from "../components/TopNav";
import "../components/landing.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getEcoTrackSubscriptions,
  openEcoTrackCustomerPortal,
} from "../utils/stripePayments";

/* ---------- small helpers ---------- */
const lbl = {
  display: "block",
  fontSize: 12,
  color: "#64748b",
  marginBottom: 6,
};

const input = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  outline: "none",
  background: "white",
};

const legalLink = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#0f172a",
  textDecoration: "none",
  fontSize: 13,
  fontWeight: 600,
};

function Card({ children, style }) {
  return (
    <div
      className="card"
      style={{
        padding: 16,
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        boxShadow: "0 6px 20px rgba(16,24,40,.06)",
        background: "#ffffff",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* =========================================================
   OPTIONS
========================================================= */

const SECTORS = [
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Agriculture/Food", label: "Agriculture/Food" },
  { value: "Textile/Fashion", label: "Textile/Fashion" },
  { value: "Tech", label: "Tech" },
  { value: "Finance", label: "Finance" },
  { value: "Construction", label: "Construction" },
  { value: "Furniture", label: "Furniture" },
  { value: "Transportation", label: "Transportation" },
];

const SIZE_OPTIONS = [
  { value: "1-9", label: "1–9" },
  { value: "1-10", label: "1–10" },
  { value: "10-49", label: "10–49" },
  { value: "11-50", label: "11–50" },
  { value: "50-249", label: "50–249" },
  { value: "51-250", label: "51–250" },
  { value: "250-999", label: "250–999" },
  { value: "251-1000", label: "251–1000" },
  { value: "1000+", label: "1000+" },
];

const REGION_OPTIONS = [
  { value: "italy", label: "Italy" },
  {
    value: "eu-excluding-italy",
    label: "European Union — excluding Italy",
  },
  { value: "outside-eu", label: "Outside the European Union" },
];

const CSRD_OPTIONS = [
  { value: "YES", label: "Yes (in scope)" },
  { value: "NO", label: "No (out of scope)" },
  { value: "UNSURE", label: "Unsure" },
  { value: "In scope", label: "In scope" },
  { value: "Out of scope", label: "Out of scope" },
  { value: "Unsure", label: "Unsure" },
];

function withSavedValue(value, options) {
  if (!value) return options;
  const exists = options.some((o) => o.value === value);
  if (exists) return options;
  return [{ value, label: `Saved value: ${value}` }, ...options];
}

function pickFirst(...vals) {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

function normalizeSavedRegion(regionValue, previousCountryValue) {
  const region = String(regionValue || "").trim();

  if (region) return region;

  const previousCountry = String(previousCountryValue || "")
    .trim()
    .toLowerCase();

  // Safe migration for the value used by the former Country field.
  if (previousCountry === "italy" || previousCountry === "italia") {
    return "italy";
  }

  return "";
}

function formatRegion(regionValue) {
  const matchedRegion = REGION_OPTIONS.find(
    (option) => option.value === regionValue
  );

  return matchedRegion?.label || regionValue || "-";
}

function deletionStorageKey(uid) {
  return `ecotrackDeletionRequestSent:${uid}`;
}

function isSubscriptionActive(subscription) {
  return ["active", "trialing"].includes(subscription?.status);
}

function isCancellationScheduled(subscription) {
  return (
    subscription?.cancel_at_period_end === true ||
    subscription?.cancel_at != null
  );
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/* ===========================
   PDF helpers - DATA EXPORT REPORT
=========================== */

const PDF_THEME = {
  green: [20, 138, 88],
  greenSoft: [234, 247, 240],
  dark: [15, 23, 42],
  muted: [100, 116, 139],
  line: [226, 232, 240],
  pale: [248, 250, 252],
  blue: [37, 99, 235],
  purple: [109, 59, 191],
  amber: [245, 158, 11],
  white: [255, 255, 255],
};

function toDateValue(value) {
  if (!value) return null;

  if (value instanceof Date) return value;

  if (value?.toDate) {
    return value.toDate();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
  const d = toDateValue(value) || value;

  if (!(d instanceof Date)) return "-";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function formatDate(value) {
  const d = toDateValue(value) || value;

  if (!(d instanceof Date)) return "-";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

function guessImgFormat(dataUrl) {
  const s = String(dataUrl || "");

  if (s.startsWith("data:image/jpeg") || s.startsWith("data:image/jpg")) {
    return "JPEG";
  }

  if (s.startsWith("data:image/webp")) return "WEBP";

  return "PNG";
}

function drawLogoKeepRatio(pdf, dataUrl, x, y, maxW, maxH) {
  if (!dataUrl) return { w: 0, h: 0 };

  const props = pdf.getImageProperties(dataUrl);
  const iw = props?.width || 1;
  const ih = props?.height || 1;
  const ratio = iw / ih;

  let w = maxW;
  let h = w / ratio;

  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }

  pdf.addImage(dataUrl, guessImgFormat(dataUrl), x, y, w, h);

  return { w, h };
}

async function toDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();

  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

function drawDivider(pdf, x1, x2, y) {
  pdf.setDrawColor(...PDF_THEME.line);
  pdf.setLineWidth(0.8);
  pdf.line(x1, y, x2, y);
}

function drawRoundedCard(
  pdf,
  x,
  y,
  width,
  height,
  fillColor = PDF_THEME.white,
  borderColor = PDF_THEME.line
) {
  pdf.setFillColor(...fillColor);
  pdf.setDrawColor(...borderColor);
  pdf.setLineWidth(0.8);
  pdf.roundedRect(x, y, width, height, 10, 10, "FD");
}

function percentageNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;

  const result = value <= 1 ? value * 100 : value;

  return Math.max(0, Math.min(100, Math.round(result)));
}

function percentageLabel(value) {
  const result = percentageNumber(value);
  return result === null ? "-" : `${result}%`;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function storedText(value) {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  return String(value);
}

function getAssessmentScores(assessment = {}) {
  const overallRaw = firstFiniteNumber(
    assessment.overallScore,
    assessment.overall,
    assessment.score,
    assessment.weightedScore,
    assessment.results?.overallScore
  );

  const environmentalRaw = firstFiniteNumber(
    assessment.envScore,
    assessment.esgScores?.E,
    assessment.esgScores?.environmental,
    assessment.pillarScores?.E,
    assessment.pillarScores?.environmental,
    assessment.scores?.environmental
  );

  const socialRaw = firstFiniteNumber(
    assessment.socScore,
    assessment.esgScores?.S,
    assessment.esgScores?.social,
    assessment.pillarScores?.S,
    assessment.pillarScores?.social,
    assessment.scores?.social
  );

  const governanceRaw = firstFiniteNumber(
    assessment.govScore,
    assessment.esgScores?.G,
    assessment.esgScores?.governance,
    assessment.pillarScores?.G,
    assessment.pillarScores?.governance,
    assessment.scores?.governance
  );

  return {
    overallValue: percentageNumber(overallRaw),
    environmentalValue: percentageNumber(environmentalRaw),
    socialValue: percentageNumber(socialRaw),
    governanceValue: percentageNumber(governanceRaw),
    overall: percentageLabel(overallRaw),
    environmental: percentageLabel(environmentalRaw),
    social: percentageLabel(socialRaw),
    governance: percentageLabel(governanceRaw),
  };
}

function assessmentSortTimestamp(assessment) {
  const d =
    toDateValue(assessment?.updatedAt) || toDateValue(assessment?.createdAt);

  return d ? d.getTime() : 0;
}

function readableActionPlanTiming(item = {}) {
  if (item.timeframe) {
    return storedText(item.timeframe);
  }

  if (item.scheduledFor) {
    return formatDate(item.scheduledFor);
  }

  return "-";
}

function extractAnswerRows(answers = {}) {
  return Object.entries(answers).map(([questionId, storedAnswer]) => {
    if (
      storedAnswer &&
      typeof storedAnswer === "object" &&
      !Array.isArray(storedAnswer)
    ) {
      const answerText = pickFirst(
        storedAnswer.label,
        storedAnswer.answer,
        storedAnswer.value,
        storedAnswer.response,
        "-"
      );

      const scoreText =
        typeof storedAnswer.score === "number"
          ? String(storedAnswer.score)
          : "-";

      return [questionId, answerText, scoreText];
    }

    return [questionId, storedText(storedAnswer), "-"];
  });
}

function drawBrand(pdf, PAGE, logoDataUrl, y) {
  if (logoDataUrl) {
    drawLogoKeepRatio(pdf, logoDataUrl, PAGE.l, y, 118, 28);
    return;
  }

  pdf.setFillColor(...PDF_THEME.green);
  pdf.circle(PAGE.l + 11, y + 11, 11, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...PDF_THEME.white);
  pdf.text("✓", PAGE.l + 11, y + 15, { align: "center" });

  pdf.setFontSize(18);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text("EcoTrack", PAGE.l + 30, y + 17);
}

function drawReportHeader(pdf, PAGE, logoDataUrl, accountEmail, exportedAt) {
  drawBrand(pdf, PAGE, logoDataUrl, 34);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(9);
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.text("DATA EXPORT REPORT", PAGE.rEdge, 42, { align: "right" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`Account: ${accountEmail || "-"}`, PAGE.rEdge, 58, {
    align: "right",
  });
  pdf.text(`Generated: ${formatDate(exportedAt)}`, PAGE.rEdge, 72, {
    align: "right",
  });
  pdf.text("Export format: PDF", PAGE.rEdge, 86, {
    align: "right",
  });
  pdf.text("Scope: Account data", PAGE.rEdge, 100, {
    align: "right",
  });

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(28);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text("My Data Export", PAGE.l, 94);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(...PDF_THEME.muted);

  const subtitle =
    "A readable copy of profile, assessment and action-plan data stored for this EcoTrack account.";

  const subtitleLines = pdf.splitTextToSize(subtitle, 300);
  pdf.text(subtitleLines, PAGE.l, 112, { lineHeightFactor: 1.45 });

  drawDivider(pdf, PAGE.l, PAGE.rEdge, 146);

  return 166;
}

function drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt) {
  drawBrand(pdf, PAGE, logoDataUrl, 28);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text("My Data Export", PAGE.rEdge, 40, { align: "right" });

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.text(`Generated ${formatDate(exportedAt)}`, PAGE.rEdge, 53, {
    align: "right",
  });

  drawDivider(pdf, PAGE.l, PAGE.rEdge, 64);
}

function addFooter(pdf, PAGE, pageNumber, pageCount) {
  const dividerY = PAGE.h - PAGE.b + 8;
  const textY = PAGE.h - PAGE.b + 26;

  drawDivider(pdf, PAGE.l, PAGE.rEdge, dividerY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(...PDF_THEME.muted);

  pdf.text("Building a sustainable future, together.", PAGE.l, textY);
  pdf.text(`Page ${pageNumber} / ${pageCount}`, PAGE.rEdge, textY, {
    align: "right",
  });
}

function sectionTitle(pdf, PAGE, text, y) {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text(text, PAGE.l, y);

  return y + 14;
}

function drawMetricCard(
  pdf,
  x,
  y,
  width,
  height,
  label,
  value,
  accentColor,
  percentage = null,
  fillColor = PDF_THEME.white
) {
  drawRoundedCard(pdf, x, y, width, height, fillColor);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(...PDF_THEME.muted);
  pdf.text(String(label).toUpperCase(), x + 14, y + 19);

  pdf.setFontSize(19);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text(storedText(value), x + 14, y + 48);

  if (percentage !== null) {
    const barX = x + 14;
    const barY = y + height - 17;
    const barW = width - 28;

    pdf.setFillColor(229, 231, 235);
    pdf.roundedRect(barX, barY, barW, 5, 2.5, 2.5, "F");

    pdf.setFillColor(...accentColor);
    pdf.roundedRect(
      barX,
      barY,
      Math.max(0, (barW * percentage) / 100),
      5,
      2.5,
      2.5,
      "F"
    );
  }
}

function ensureSpace(pdf, PAGE, currentY, requiredHeight, logoDataUrl, exportedAt) {
  const contentBottom = PAGE.h - PAGE.b - 24;

  if (currentY + requiredHeight <= contentBottom) {
    return currentY;
  }

  pdf.addPage();
  drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);

  return 88;
}

function drawExportNote(pdf, PAGE, y) {
  const noteText =
    "Important: this export is an account-data copy for user transparency. It is not an audit record, ESG certification, assurance statement or compliance report. Results depend on the information provided by the user.";

  const lines = pdf.splitTextToSize(noteText, PAGE.rEdge - PAGE.l - 38);
  const height = 30 + lines.length * 13;

  drawRoundedCard(
    pdf,
    PAGE.l,
    y,
    PAGE.rEdge - PAGE.l,
    height,
    PDF_THEME.greenSoft,
    PDF_THEME.greenSoft
  );

  pdf.setFillColor(...PDF_THEME.green);
  pdf.roundedRect(PAGE.l, y, 5, height, 2, 2, "F");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...PDF_THEME.dark);
  pdf.text("Important:", PAGE.l + 18, y + 21);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(51, 65, 85);
  pdf.text(lines, PAGE.l + 18, y + 38, { lineHeightFactor: 1.35 });

  return y + height;
}

/* ===========================
   COMPONENT
=========================== */
export default function ProfileSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const [sector, setSector] = useState("");
  const [size, setSize] = useState("");
  const [region, setRegion] = useState("");
  const [csrd, setCsrd] = useState("");
  const [goal, setGoal] = useState("");
  const [timeline, setTimeline] = useState("");

  const [remindAssessments, setRemindAssessments] = useState(false);

  const [requestingDeletion, setRequestingDeletion] = useState(false);
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const [checkingCancellation, setCheckingCancellation] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");
  const [deletionPopup, setDeletionPopup] = useState(null);
  const [deletionRequestKnownSent, setDeletionRequestKnownSent] =
    useState(false);

  useEffect(() => {
    (async () => {
      const u = auth.currentUser;

      if (!u) {
        setLoading(false);
        return;
      }

      setEmail(u.email || "");

      try {
        const storedDeletionRequest = window.localStorage.getItem(
          deletionStorageKey(u.uid)
        );

        if (storedDeletionRequest === "true") {
          setDeletionRequestKnownSent(true);
        }
      } catch {
        // localStorage may be unavailable in some browsers/settings.
      }

      const ref = doc(db, "users", u.uid);
      const snap = await getDoc(ref);

      if (snap.exists()) {
        const d = snap.data() || {};
        const p = d.profile || {};
        const s = d.settings || {};
        const ob = d.onboarding || d.onboardingProfile || {};

        setName(
          pickFirst(d.name, p.name, d.profile?.name, u.email?.split("@")[0], "")
        );

        setSector(pickFirst(p.sector, d.sector, ob.sector, ""));
        setSize(
          pickFirst(
            p.size,
            d.size,
            d.companySize,
            ob.size,
            ob.companySize,
            ""
          )
        );
        setRegion(
          normalizeSavedRegion(
            pickFirst(p.region, d.region, ob.region, ""),
            pickFirst(p.country, d.country, ob.country, "")
          )
        );
        setCsrd(
          pickFirst(
            p.csrd,
            d.csrd,
            d.csrdStatus,
            ob.csrd,
            ob.csrdStatus,
            ""
          )
        );
        setGoal(pickFirst(p.goal, d.goal, d.goalShort, ob.goal, ""));
        setTimeline(pickFirst(p.timeline, d.timeline, ob.timeline, ""));

        setRemindAssessments(!!s.remindAssessments);
      } else {
        setName(u.email?.split("@")[0] || "");
      }

      setLoading(false);
    })();
  }, []);

  const clearMessages = () => {
    setErr("");
    setMsg("");
    setDeleteErr("");
  };

  const handleSave = async () => {
    clearMessages();
    setSaving(true);

    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");

      const ref = doc(db, "users", u.uid);
      const snap = await getDoc(ref);

      const payload = {
        uid: u.uid,
        email: u.email || "",
        name: name?.trim() || "",
        profile: {
          sector: sector || "",
          size: size || "",
          region: region || "",
          csrd: csrd || "",
          goal: goal || "",
          timeline: timeline || "",
        },
        settings: {
          remindAssessments: !!remindAssessments,
        },
        updatedAt: serverTimestamp(),
      };

      if (!snap.exists()) {
        await setDoc(ref, {
          ...payload,
          onboardingCompleted: true,
          createdAt: serverTimestamp(),
        });
      } else {
        await updateDoc(ref, payload);
      }

      setMsg("Profile saved.");
    } catch (e) {
      console.error("Profile save error:", e);
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    clearMessages();

    try {
      const u = auth.currentUser;
      const targetEmail = email || u?.email || "";

      if (!targetEmail) throw new Error("No email found for this account.");

      await sendPasswordResetEmail(auth, targetEmail);
      setMsg(`Password reset email sent to ${targetEmail}.`);
    } catch (e) {
      console.error("Password reset error:", e);

      let message = e.message || "Failed to send reset email";

      if (e.code === "auth/invalid-email") {
        message = "The account email address is invalid.";
      } else if (e.code === "auth/user-not-found") {
        message =
          "No user found with this email in Firebase Authentication. Check the email or create the user first.";
      } else if (e.code === "auth/unauthorized-domain") {
        message =
          "This domain is not authorized in Firebase Authentication. Add localhost or your domain to the Authorized domains list.";
      } else if (e.code === "auth/operation-not-allowed") {
        message =
          "Email/password sign-in is disabled in Firebase Authentication. Enable it in the console.";
      }

      setErr(message);
    }
  };

  const hasKnownDeletionRequest = (uid) => {
    let alreadyRequested = deletionRequestKnownSent;

    try {
      alreadyRequested =
        alreadyRequested ||
        window.localStorage.getItem(deletionStorageKey(uid)) === "true";
    } catch {
      // localStorage may be unavailable. Continue with server handling.
    }

    return alreadyRequested;
  };

  const submitAccountDeletionRequest = async (note) => {
    const u = auth.currentUser;

    if (!u) {
      setDeleteErr("Not authenticated.");
      setDeletionPopup({
        type: "info",
        title: "Login required",
        message: "You need to be logged in to request account deletion.",
      });
      return;
    }

    if (hasKnownDeletionRequest(u.uid)) {
      setDeletionPopup({
        type: "info",
        title: "Request already sent",
        message:
          "Your account deletion request has already been sent and will be handled by Viridis.",
      });
      return;
    }

    setRequestingDeletion(true);

    try {
      const requestAccountDeletion = httpsCallable(
        functions,
        "requestAccountDeletion"
      );

      const result = await requestAccountDeletion({ note });

      const successMessage =
        result.data?.message ||
        "Your account deletion request has been submitted and will be handled by Viridis.";

      try {
        window.localStorage.setItem(deletionStorageKey(u.uid), "true");
      } catch {
        // localStorage may be unavailable. The server request still succeeded.
      }

      setDeletionRequestKnownSent(true);
      setMsg(successMessage);
      setDeletionPopup({
        type: "info",
        title: "Request submitted",
        message: successMessage,
      });
    } catch (e) {
      console.error("Account deletion request error:", e);

      const message =
        "We could not submit your deletion request. Please contact info@viridisconsultancy.com.";

      setDeleteErr(message);
      setDeletionPopup({
        type: "info",
        title: "Something went wrong",
        message,
      });
    } finally {
      setRequestingDeletion(false);
    }
  };

  const handleRequestAccountDeletion = () => {
    clearMessages();

    const u = auth.currentUser;

    if (!u) {
      setDeleteErr("Not authenticated.");
      setDeletionPopup({
        type: "info",
        title: "Login required",
        message: "You need to be logged in to request account deletion.",
      });
      return;
    }

    if (hasKnownDeletionRequest(u.uid)) {
      setDeletionPopup({
        type: "info",
        title: "Request already sent",
        message:
          "Your account deletion request has already been sent and will be handled by Viridis.",
      });
      return;
    }

    setDeletionPopup({
      type: "closure-confirmation",
      title: "Close account and cancel subscription?",
      message:
        "This process has two steps. First, Stripe will open so you can securely cancel your subscription renewal. If Stripe confirms that cancellation has been scheduled, EcoTrack will submit your account deletion request to Viridis. Your access remains active until the end of any already-paid billing period. If you have no active subscription, the deletion request will be submitted directly.",
    });
  };

  const handleContinueToStripe = async () => {
    clearMessages();
    setDeletionPopup(null);
    setOpeningBillingPortal(true);

    try {
await openEcoTrackCustomerPortal(
  `${window.location.origin}/esg-assessment/profile?accountClosure=stripe-return`
);
    } catch (e) {
      console.error("Account closure / Stripe portal error:", e);

      const message =
        e.message ||
        "We could not open Stripe subscription management. Please try again.";

      setDeleteErr(message);
      setDeletionPopup({
        type: "info",
        title: "Unable to continue",
        message,
      });
    } finally {
      setOpeningBillingPortal(false);
    }
  };

const verifyStripeCancellationAfterReturn = async () => {
  clearMessages();
  setCheckingCancellation(true);

  try {
    await submitAccountDeletionRequest(
      "User returned from Stripe after managing subscription. Account deletion request submitted for backend validation."
    );
  } catch (e) {
    console.error("Account deletion after Stripe return error:", e);

    const message =
      "We could not submit your account deletion request after returning from Stripe. Please try again or contact info@viridisconsultancy.com.";

    setDeleteErr(message);
    setDeletionPopup({
      type: "info",
      title: "Unable to submit request",
      message,
    });
  } finally {
    setCheckingCancellation(false);
  }
};

  useEffect(() => {
    if (loading) return;

    const params = new URLSearchParams(window.location.search);

    if (params.get("accountClosure") !== "stripe-return") return;

    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${window.location.hash || ""}`
    );

    verifyStripeCancellationAfterReturn();
    // Runs only when the page has loaded after returning from Stripe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const handleDownloadData = async () => {
    clearMessages();
    setExporting(true);

    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Not authenticated");

      const uid = u.uid;
      const exportedAt = new Date();

      /* =========================================================
         LOAD STORED USER DATA
      ========================================================= */

      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() || {} : {};

      const profile = userData.profile || {};
      const settings = userData.settings || {};

      /* =========================================================
         LOAD STORED ASSESSMENTS
      ========================================================= */

      const assessmentsRef = collection(db, "users", uid, "assessments");
      const assessmentsSnap = await getDocs(assessmentsRef);

      const assessments = assessmentsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      assessments.sort(
        (a, b) => assessmentSortTimestamp(b) - assessmentSortTimestamp(a)
      );

      const submittedAssessments = assessments.filter(
        (assessment) =>
          String(assessment.status || "").toLowerCase() === "submitted"
      );

      const latestAssessment =
        submittedAssessments[0] || assessments[0] || null;

      const latestScores = latestAssessment
        ? getAssessmentScores(latestAssessment)
        : null;

      /* =========================================================
         LOAD STORED ACTION PLAN ITEMS
      ========================================================= */

      const actionPlanRef = collection(db, "users", uid, "actionPlan");
      const actionPlanSnap = await getDocs(actionPlanRef);

      const actionPlanItems = actionPlanSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

      actionPlanItems.sort((a, b) => {
        const ad = toDateValue(a.createdAt);
        const bd = toDateValue(b.createdAt);

        return (bd?.getTime() || 0) - (ad?.getTime() || 0);
      });

      /* =========================================================
         LOAD SUBSCRIPTION STATUS
         This is shown as account information only.
         It does not alter the subscription.
      ========================================================= */

      let accountStatus = "Unavailable";

      try {
        const subscriptions = await getEcoTrackSubscriptions();
        const activeSubscription = subscriptions.find(isSubscriptionActive);

        if (activeSubscription) {
          accountStatus = isCancellationScheduled(activeSubscription)
            ? "Active - renewal cancelled"
            : "Active";
        } else if (
          subscriptions.some(
            (subscription) => subscription.status === "canceled"
          )
        ) {
          accountStatus = "Cancelled";
        } else {
          accountStatus = "No active subscription";
        }
      } catch (subscriptionError) {
        console.warn(
          "Subscription status could not be loaded for data export:",
          subscriptionError
        );
      }

      /* =========================================================
         LOAD LOGO
      ========================================================= */

      const logoUrl = new URL(
        "../assets/ecotrack-logo.png",
        import.meta.url
      ).href;

      let logoDataUrl = "";

      try {
        logoDataUrl = await toDataUrl(logoUrl);
      } catch {
        logoDataUrl = "";
      }

      /* =========================================================
         CREATE PDF
      ========================================================= */

      const pdf = new jsPDF({
        compress: true,
        unit: "pt",
        format: "a4",
      });

      pdf.setProperties({
        title: "EcoTrack - My Data Export",
        author: "EcoTrack by Viridis",
        subject: "Readable copy of user profile, assessment and action-plan data",
        creator: "EcoTrack by Viridis",
      });

      const PAGE = {
        w: pdf.internal.pageSize.getWidth(),
        h: pdf.internal.pageSize.getHeight(),
        l: 46,
        r: 46,
        t: 74,
        b: 54,
        get rEdge() {
          return this.w - this.r;
        },
      };

      const accountEmail = userData.email || u.email || "-";
      const accountName = userData.name || profile.name || name || "-";

      let y = drawReportHeader(
        pdf,
        PAGE,
        logoDataUrl,
        accountEmail,
        exportedAt
      );

      /* =========================================================
         SUMMARY CARDS
      ========================================================= */

      const availableWidth = PAGE.rEdge - PAGE.l;
      const cardsGap = 10;
      const metricWidth = (availableWidth - cardsGap * 2) / 3;
      const metricHeight = 78;

      drawMetricCard(
        pdf,
        PAGE.l,
        y,
        metricWidth,
        metricHeight,
        "Account status",
        accountStatus,
        PDF_THEME.green,
        accountStatus.startsWith("Active") ? 100 : null,
        PDF_THEME.greenSoft
      );

      drawMetricCard(
        pdf,
        PAGE.l + metricWidth + cardsGap,
        y,
        metricWidth,
        metricHeight,
        "Assessments saved",
        String(assessments.length),
        PDF_THEME.blue,
        assessments.length > 0 ? Math.min(100, assessments.length * 15) : 0
      );

      drawMetricCard(
        pdf,
        PAGE.l + (metricWidth + cardsGap) * 2,
        y,
        metricWidth,
        metricHeight,
        "Action plan items",
        String(actionPlanItems.length),
        PDF_THEME.purple,
        actionPlanItems.length > 0
          ? Math.min(100, actionPlanItems.length * 8)
          : 0
      );

      y += metricHeight + 30;

      /* =========================================================
         PROFILE INFORMATION
      ========================================================= */

      y = sectionTitle(pdf, PAGE, "Profile information", y);

      const profileRows = [
        [
          "Name",
          accountName,
          "Stored account or profile value",
        ],
        [
          "Email",
          accountEmail,
          "Authentication and account contact email",
        ],
        [
          "Sector",
          storedText(profile.sector),
          "Used to select the questionnaire and sector-based scoring logic",
        ],
        [
          "Company size",
          storedText(profile.size),
          "Self-declared profile context",
        ],
        [
          "Region",
          formatRegion(
            normalizeSavedRegion(
              pickFirst(profile.region, userData.region, ""),
              pickFirst(profile.country, userData.country, "")
            )
          ),
          "Self-declared company location",
        ],
        [
          "CSRD status",
          storedText(profile.csrd),
          "Self-declared information",
        ],
        [
          "Sustainability goal",
          storedText(profile.goal),
          "Stored profile context",
        ],
        [
          "Timeline",
          storedText(profile.timeline),
          "Stored profile context",
        ],
        [
          "Assessment reminder",
          settings.remindAssessments ? "Enabled" : "Disabled",
          "In-app preference",
        ],
      ];

      const legacyCountryValue = pickFirst(
        profile.country,
        userData.country,
        ""
      );

      const legacyTurnoverValue = pickFirst(
        profile.turnover,
        userData.turnover,
        ""
      );

      if (legacyCountryValue) {
        profileRows.push([
          "Legacy stored country value",
          legacyCountryValue,
          "Previously stored field retained in account data",
        ]);
      }

      if (legacyTurnoverValue) {
        profileRows.push([
          "Legacy stored turnover value",
          legacyTurnoverValue,
          "Previously stored field retained in account data",
        ]);
      }

      autoTable(pdf, {
        startY: y + 8,
        head: [["Field", "Stored value", "Notes"]],
        body: profileRows,
        theme: "plain",
        styles: {
          font: "helvetica",
          fontSize: 8.5,
          cellPadding: 8,
          textColor: PDF_THEME.dark,
          overflow: "linebreak",
          valign: "top",
          lineColor: PDF_THEME.line,
          lineWidth: 0.5,
        },
        headStyles: {
          fillColor: PDF_THEME.pale,
          textColor: [71, 85, 105],
          fontStyle: "bold",
          fontSize: 8,
        },
        bodyStyles: {
          fillColor: PDF_THEME.white,
        },
        columnStyles: {
          0: { cellWidth: 116, fontStyle: "bold" },
          1: { cellWidth: 145 },
          2: { cellWidth: availableWidth - 261 },
        },
        margin: {
          left: PAGE.l,
          right: PAGE.r,
          top: 78,
          bottom: 64,
        },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
          }
        },
      });

      y = pdf.lastAutoTable.finalY + 30;

      /* =========================================================
         LATEST ESG ASSESSMENT SUMMARY
      ========================================================= */

      y = ensureSpace(pdf, PAGE, y, 170, logoDataUrl, exportedAt);
      y = sectionTitle(pdf, PAGE, "Latest ESG assessment summary", y);

      if (!latestAssessment) {
        drawRoundedCard(
          pdf,
          PAGE.l,
          y + 8,
          availableWidth,
          52,
          PDF_THEME.pale
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(...PDF_THEME.muted);
        pdf.text("No ESG assessments are currently stored for this account.", PAGE.l + 16, y + 39);

        y += 78;
      } else {
        const summaryCardWidth = (availableWidth - 10) / 2;

        drawMetricCard(
          pdf,
          PAGE.l,
          y + 8,
          summaryCardWidth,
          76,
          "Overall weighted ESG score",
          latestScores.overall,
          PDF_THEME.green,
          latestScores.overallValue
        );

        drawMetricCard(
          pdf,
          PAGE.l + summaryCardWidth + 10,
          y + 8,
          summaryCardWidth,
          76,
          "Assessment status",
          storedText(latestAssessment.status),
          PDF_THEME.amber,
          null
        );

        y += 104;

        const latestSummaryRows = [
          [
            "Environmental",
            latestScores.environmental,
          ],
          [
            "Social",
            latestScores.social,
          ],
          [
            "Governance",
            latestScores.governance,
          ],
          [
            "Assessment date",
            formatDate(
              latestAssessment.updatedAt || latestAssessment.createdAt
            ),
          ],
          [
            "Sector",
            storedText(latestAssessment.sector || profile.sector),
          ],
        ];

        autoTable(pdf, {
          startY: y,
          head: [["Stored assessment field", "Value"]],
          body: latestSummaryRows,
          theme: "plain",
          styles: {
            font: "helvetica",
            fontSize: 9,
            cellPadding: 8,
            textColor: PDF_THEME.dark,
            overflow: "linebreak",
            lineColor: PDF_THEME.line,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: PDF_THEME.pale,
            textColor: [71, 85, 105],
            fontStyle: "bold",
            fontSize: 8,
          },
          columnStyles: {
            0: { cellWidth: 190, fontStyle: "bold" },
            1: { cellWidth: availableWidth - 190 },
          },
          margin: {
            left: PAGE.l,
            right: PAGE.r,
            top: 78,
            bottom: 64,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
            }
          },
        });

        y = pdf.lastAutoTable.finalY + 30;
      }

      /* =========================================================
         STORED ACTION PLAN
      ========================================================= */

      y = ensureSpace(pdf, PAGE, y, 125, logoDataUrl, exportedAt);
      y = sectionTitle(pdf, PAGE, "Stored action plan", y);

      if (actionPlanItems.length === 0) {
        drawRoundedCard(
          pdf,
          PAGE.l,
          y + 8,
          availableWidth,
          52,
          PDF_THEME.pale
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(...PDF_THEME.muted);
        pdf.text(
          "No saved action-plan items are currently stored for this account.",
          PAGE.l + 16,
          y + 39
        );

        y += 78;
      } else {
        const actionRows = actionPlanItems.map((item, index) => [
          String(index + 1),
          storedText(item.text),
          storedText(item.tags),
          item.completed ? "Completed" : "Open",
          readableActionPlanTiming(item),
        ]);

        autoTable(pdf, {
          startY: y + 8,
          head: [["#", "Action", "Tags", "Status", "Timing"]],
          body: actionRows,
          theme: "plain",
          styles: {
            font: "helvetica",
            fontSize: 8.2,
            cellPadding: 7,
            textColor: PDF_THEME.dark,
            overflow: "linebreak",
            valign: "top",
            lineColor: PDF_THEME.line,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: PDF_THEME.pale,
            textColor: [71, 85, 105],
            fontStyle: "bold",
            fontSize: 8,
          },
          columnStyles: {
            0: { cellWidth: 24 },
            1: { cellWidth: 214 },
            2: { cellWidth: 110 },
            3: { cellWidth: 68 },
            4: { cellWidth: availableWidth - 416 },
          },
          margin: {
            left: PAGE.l,
            right: PAGE.r,
            top: 78,
            bottom: 64,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
            }
          },
        });

        y = pdf.lastAutoTable.finalY + 30;
      }

      /* =========================================================
         ASSESSMENT HISTORY
      ========================================================= */

      y = ensureSpace(pdf, PAGE, y, 125, logoDataUrl, exportedAt);
      y = sectionTitle(pdf, PAGE, "Stored assessment history", y);

      if (assessments.length === 0) {
        drawRoundedCard(
          pdf,
          PAGE.l,
          y + 8,
          availableWidth,
          52,
          PDF_THEME.pale
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);
        pdf.setTextColor(...PDF_THEME.muted);
        pdf.text("No stored assessment history is available.", PAGE.l + 16, y + 39);

        y += 78;
      } else {
        const assessmentRows = assessments.map((assessment, index) => {
          const scores = getAssessmentScores(assessment);

          return [
            String(index + 1),
            storedText(assessment.status),
            storedText(assessment.sector || profile.sector),
            formatDate(assessment.updatedAt || assessment.createdAt),
            scores.overall,
            scores.environmental,
            scores.social,
            scores.governance,
          ];
        });

        autoTable(pdf, {
          startY: y + 8,
          head: [["#", "Status", "Sector", "Date", "Overall", "E", "S", "G"]],
          body: assessmentRows,
          theme: "plain",
          styles: {
            font: "helvetica",
            fontSize: 7.8,
            cellPadding: 6,
            textColor: PDF_THEME.dark,
            overflow: "linebreak",
            valign: "middle",
            lineColor: PDF_THEME.line,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: PDF_THEME.pale,
            textColor: [71, 85, 105],
            fontStyle: "bold",
            fontSize: 7.5,
          },
          columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 62 },
            2: { cellWidth: 102 },
            3: { cellWidth: 72 },
            4: { cellWidth: 58 },
            5: { cellWidth: 47 },
            6: { cellWidth: 47 },
            7: { cellWidth: 47 },
          },
          margin: {
            left: PAGE.l,
            right: PAGE.r,
            top: 78,
            bottom: 64,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
            }
          },
        });

        y = pdf.lastAutoTable.finalY + 30;
      }

      /* =========================================================
         EXPORT NOTE
      ========================================================= */

      y = ensureSpace(pdf, PAGE, y, 92, logoDataUrl, exportedAt);
      y = sectionTitle(pdf, PAGE, "Data export note", y);
      drawExportNote(pdf, PAGE, y + 8);

      /* =========================================================
         STORED ANSWER DETAILS - ONE SECTION PER ASSESSMENT
      ========================================================= */

      assessments.forEach((assessment, index) => {
        pdf.addPage();
        drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);

        let detailY = 92;

        detailY = sectionTitle(
          pdf,
          PAGE,
          `Assessment ${index + 1} - Stored details`,
          detailY
        );

        const scores = getAssessmentScores(assessment);

        const assessmentMetaRows = [
          ["Assessment ID", assessment.id],
          ["Status", storedText(assessment.status)],
          ["Sector", storedText(assessment.sector || profile.sector)],
          ["Created", formatDateTime(assessment.createdAt)],
          ["Updated", formatDateTime(assessment.updatedAt)],
          ["Overall score", scores.overall],
          ["Environmental score", scores.environmental],
          ["Social score", scores.social],
          ["Governance score", scores.governance],
        ];

        autoTable(pdf, {
          startY: detailY + 8,
          head: [["Field", "Stored value"]],
          body: assessmentMetaRows,
          theme: "plain",
          styles: {
            font: "helvetica",
            fontSize: 8.7,
            cellPadding: 7,
            textColor: PDF_THEME.dark,
            overflow: "linebreak",
            lineColor: PDF_THEME.line,
            lineWidth: 0.5,
          },
          headStyles: {
            fillColor: PDF_THEME.pale,
            textColor: [71, 85, 105],
            fontStyle: "bold",
            fontSize: 8,
          },
          columnStyles: {
            0: { cellWidth: 150, fontStyle: "bold" },
            1: { cellWidth: availableWidth - 150 },
          },
          margin: {
            left: PAGE.l,
            right: PAGE.r,
            top: 78,
            bottom: 64,
          },
          didDrawPage: (data) => {
            if (data.pageNumber > 1) {
              drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
            }
          },
        });

        detailY = pdf.lastAutoTable.finalY + 28;
        detailY = ensureSpace(
          pdf,
          PAGE,
          detailY,
          90,
          logoDataUrl,
          exportedAt
        );

        detailY = sectionTitle(pdf, PAGE, "Stored answers", detailY);

        const answerRows = extractAnswerRows(assessment.answers || {});

        if (answerRows.length === 0) {
          drawRoundedCard(
            pdf,
            PAGE.l,
            detailY + 8,
            availableWidth,
            50,
            PDF_THEME.pale
          );

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(10);
          pdf.setTextColor(...PDF_THEME.muted);
          pdf.text(
            "No individual answers are stored for this assessment.",
            PAGE.l + 16,
            detailY + 38
          );
        } else {
          autoTable(pdf, {
            startY: detailY + 8,
            head: [["Question ID", "Stored answer", "Stored score"]],
            body: answerRows,
            theme: "plain",
            styles: {
              font: "helvetica",
              fontSize: 8.2,
              cellPadding: 7,
              textColor: PDF_THEME.dark,
              overflow: "linebreak",
              valign: "top",
              lineColor: PDF_THEME.line,
              lineWidth: 0.5,
            },
            headStyles: {
              fillColor: PDF_THEME.pale,
              textColor: [71, 85, 105],
              fontStyle: "bold",
              fontSize: 8,
            },
            columnStyles: {
              0: { cellWidth: 112, fontStyle: "bold" },
              1: { cellWidth: availableWidth - 177 },
              2: { cellWidth: 65, halign: "center" },
            },
            margin: {
              left: PAGE.l,
              right: PAGE.r,
              top: 78,
              bottom: 64,
            },
            didDrawPage: (data) => {
              if (data.pageNumber > 1) {
                drawContinuationHeader(pdf, PAGE, logoDataUrl, exportedAt);
              }
            },
          });
        }
      });

      /* =========================================================
         FOOTERS AND DOWNLOAD
      ========================================================= */

      const pageCount = pdf.getNumberOfPages();

      for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
        pdf.setPage(pageNumber);
        addFooter(pdf, PAGE, pageNumber, pageCount);
      }

      const dateStr = exportedAt.toISOString().split("T")[0];
      pdf.save(`EcoTrack_My_Data_Export_${dateStr}.pdf`);

      setMsg("Data exported as PDF.");
    } catch (e) {
      console.error("Data export error:", e);
      setErr(e.message || "Failed to download data.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="landing" style={{ alignItems: "center" }}>
        <main
          className="landing__main"
          style={{ maxWidth: 1200, width: "100%", paddingTop: 80 }}
        >
          <TopNav />
          <div>Loading…</div>
        </main>
      </div>
    );
  }

  return (
    <div className="landing" style={{ alignItems: "stretch" }}>
      <main
        className="landing__main"
        style={{ maxWidth: 1200, width: "100%", paddingTop: 80 }}
      >
        <TopNav />

        <div style={{ display: "grid", gap: 16 }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <h1 className="landing__title">Profile &amp; Settings</h1>
            <div className="landing__subtitle" style={{ opacity: 0.8 }}>
              Signed in as <b>{email}</b>
            </div>
          </div>

          {/* General */}
          <Card>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
              General
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <div>
                <label style={lbl}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={input}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label style={lbl}>Where is your company based?</label>
                <select
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  style={input}
                >
                  <option value="">Select…</option>
                  {withSavedValue(region, REGION_OPTIONS).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>Sector</label>
                <select
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  style={input}
                >
                  <option value="">Select…</option>
                  {withSavedValue(sector, SECTORS).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>Company size</label>
                <select
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  style={input}
                >
                  <option value="">Select…</option>
                  {withSavedValue(size, SIZE_OPTIONS).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>CSRD status</label>
                <select
                  value={csrd}
                  onChange={(e) => setCsrd(e.target.value)}
                  style={input}
                >
                  <option value="">Select…</option>
                  {withSavedValue(csrd, CSRD_OPTIONS).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Sustainability goal</label>
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  style={input}
                  placeholder="e.g. compliance"
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Timeline</label>
                <input
                  type="text"
                  value={timeline}
                  onChange={(e) => setTimeline(e.target.value)}
                  style={input}
                  placeholder="e.g. 6-12"
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                className="btn btn--primary"
                onClick={handleSave}
                disabled={saving}
                type="button"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>

            {(msg || err) && (
              <div style={{ marginTop: 10 }}>
                {msg && (
                  <span style={{ color: "#065f46", fontSize: 13 }}>{msg}</span>
                )}
                {err && (
                  <span style={{ color: "#b91c1c", fontSize: 13 }}>{err}</span>
                )}
              </div>
            )}
          </Card>

          {/* Security */}
          <Card>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
              Security
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                className="btn btn--ghost"
                type="button"
                onClick={handleResetPassword}
              >
                Send password reset email
              </button>

              <span style={{ fontSize: 12, color: "#64748b" }}>
                To logout, use the menu in the top-right corner.
              </span>
            </div>

            <div
              style={{
                marginTop: 16,
                paddingTop: 12,
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: "#b91c1c",
                }}
              >
                Attention
              </div>

              <p
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  marginBottom: 8,
                  maxWidth: 620,
                }}
              >
                To close your EcoTrack account, any active subscription renewal
                must first be cancelled securely through Stripe. Once cancellation
                is confirmed, a deletion request is sent to Viridis. Your access
                remains active until the end of an already-paid billing period.
              </p>

              <button
                className="btn btn--ghost"
                type="button"
                onClick={handleRequestAccountDeletion}
                disabled={
                  requestingDeletion ||
                  openingBillingPortal ||
                  checkingCancellation
                }
                style={{
                  borderColor: "#b91c1c",
                  color: "#b91c1c",
                  opacity:
                    requestingDeletion ||
                    openingBillingPortal ||
                    checkingCancellation
                      ? 0.7
                      : 1,
                }}
              >
                {requestingDeletion
                  ? "Submitting request…"
                  : openingBillingPortal
                  ? "Opening Stripe…"
                  : checkingCancellation
                  ? "Checking Stripe cancellation…"
                  : "Close account and cancel subscription"}
              </button>

              {deleteErr && (
                <p style={{ marginTop: 8, fontSize: 12, color: "#b91c1c" }}>
                  {deleteErr}
                </p>
              )}
            </div>
          </Card>

          {/* Preferences */}
        <Card>
  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
    Preferences
  </div>

  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
    <div style={{ marginTop: 8 }}>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          color: "#0f172a",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={remindAssessments}
          onChange={(e) => setRemindAssessments(e.target.checked)}
          style={{ cursor: "pointer" }}
        />
        <span>Remind me to repeat the ESG assessment periodically</span>
      </label>

      <p
        style={{
          fontSize: 11,
          color: "#64748b",
          marginTop: 4,
          marginLeft: 24,
        }}
      >
        We&apos;ll show a reminder banner inside EcoTrack when it&apos;s
        time to repeat your ESG assessment. You can disable this at
        any time.
      </p>

      <div style={{ display: "flex", gap: 8, marginTop: 12, marginLeft: 24 }}>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
          type="button"
        >
          {saving ? "Saving…" : "Save reminder preference"}
        </button>
      </div>

      {(msg || err) && (
        <div style={{ marginTop: 10, marginLeft: 24 }}>
          {msg && (
            <span style={{ color: "#065f46", fontSize: 13 }}>{msg}</span>
          )}
          {err && (
            <span style={{ color: "#b91c1c", fontSize: 13 }}>{err}</span>
          )}
        </div>
      )}
    </div>
  </div>
</Card>

          {/* Data & Privacy */}
          <Card>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              Data &amp; Privacy
            </div>

            <p
              style={{
                fontSize: 13,
                color: "#64748b",
                marginBottom: 12,
                maxWidth: 720,
              }}
            >
              You are in control of your data. You can update your profile at
              any time. You can download a copy of your EcoTrack profile and ESG
              assessments. Account deletion is handled through a deletion
              request so that subscription status, billing records, and legal
              retention requirements can be reviewed correctly.
            </p>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="btn btn--ghost"
                type="button"
                onClick={handleDownloadData}
                disabled={exporting}
              >
                {exporting ? "Preparing download…" : "Download my data"}
              </button>

              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                Downloads a PDF report with your profile and ESG assessments.
              </span>
            </div>
          </Card>

          {/* Legal documents */}
          <Card>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              Legal documents
            </div>

            <p
              style={{
                fontSize: 13,
                color: "#64748b",
                marginBottom: 12,
                maxWidth: 720,
              }}
            >
              Review the legal documents that apply to your EcoTrack account,
              subscription, data processing, business access, and use of the
              service.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <Link
                to="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                style={legalLink}
              >
                Terms and Conditions <span>↗</span>
              </Link>

              <Link
                to="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={legalLink}
              >
                Privacy Policy <span>↗</span>
              </Link>

              <Link
                to="/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={legalLink}
              >
                Cookie Policy <span>↗</span>
              </Link>

             

              <Link
                to="/refund-policy"
                target="_blank"
                rel="noopener noreferrer"
                style={legalLink}
              >
                Refund Policy <span>↗</span>
              </Link>

              <Link
                to="/legal-notice"
                target="_blank"
                rel="noopener noreferrer"
                style={legalLink}
              >
                Legal Notice <span>↗</span>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      {deletionPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#ffffff",
              borderRadius: 18,
              padding: 24,
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
              border: "1px solid #e2e8f0",
            }}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: 10,
                color: "#111827",
                fontSize: 20,
              }}
            >
              {deletionPopup.title}
            </h3>

            <p
              style={{
                color: "#475569",
                fontSize: 15,
                lineHeight: 1.6,
                marginBottom: 22,
              }}
            >
              {deletionPopup.message}
            </p>

            {deletionPopup.type === "closure-confirmation" ? (
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setDeletionPopup(null)}
                  style={{
                    flex: 1,
                    padding: "11px 14px",
                    borderRadius: 12,
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#334155",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleContinueToStripe}
                  disabled={openingBillingPortal}
                  style={{
                    flex: 1,
                    padding: "11px 14px",
                    borderRadius: 12,
                    border: "none",
                    background: "#148A58",
                    color: "white",
                    fontWeight: 700,
                    cursor: openingBillingPortal ? "not-allowed" : "pointer",
                    opacity: openingBillingPortal ? 0.7 : 1,
                  }}
                >
                  Continue to Stripe
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setDeletionPopup(null)}
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 12,
                  border: "none",
                  background: "#148A58",
                  color: "white",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}











